import React, { useEffect, useRef, useState } from 'react';
import type { XeroTenant } from '../types';
import { xeroApi } from '../api/client';

interface OrgSelectorProps {
  tenants: XeroTenant[];
  onTenantChange: (tenant: XeroTenant) => void;
}

export default function OrgSelector({ tenants, onTenantChange }: OrgSelectorProps) {
  const [selectedTenantId, setSelectedTenantId] = useState(
    tenants.find((t) => t.isActive)?.tenantId ?? tenants[0]?.tenantId ?? ''
  );
  const active = tenants.find((t) => t.tenantId === selectedTenantId) ?? tenants.find((t) => t.isActive) ?? tenants[0];
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const nextActive = tenants.find((t) => t.isActive)?.tenantId ?? tenants[0]?.tenantId ?? '';
    setSelectedTenantId(nextActive);
  }, [tenants]);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  if (tenants.length === 0) return null;

  // Single org — show as a read-only badge, no dropdown needed
  if (tenants.length === 1) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 shadow-sm">
        <XeroIcon />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Xero Organisation</p>
          <p className="text-sm font-semibold text-slate-950 truncate">{active?.tenantName}</p>
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          Connected
        </span>
      </div>
    );
  }

  // Multiple orgs — show dropdown
  async function handleChange(tenantId: string) {
    const tenant = tenants.find((t) => t.tenantId === tenantId);
    if (!tenant || tenant.tenantId === selectedTenantId) {
      setOpen(false);
      return;
    }

    setSelectedTenantId(tenantId);
    setSaving(true);
    setOpen(false);
    try {
      await xeroApi.selectTenant(tenantId);
      onTenantChange({ ...tenant, isActive: true });
    } catch (err) {
      console.warn('Failed to switch Xero organisation:', err);
      setSelectedTenantId(tenants.find((t) => t.isActive)?.tenantId ?? tenants[0]?.tenantId ?? '');
      setOpen(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 shadow-sm">
      <XeroIcon />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
          Xero Organisation
        </p>
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => !saving && setOpen((value) => !value)}
            disabled={saving}
            className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white py-1.5 pl-3 pr-8 text-left text-sm font-semibold text-slate-950 shadow-sm transition-colors hover:bg-slate-50 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:opacity-60"
          >
            <span className="truncate">{active?.tenantName}</span>
          </button>
          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
            {saving ? (
              <svg className="h-3.5 w-3.5 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          {open && (
            <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border border-slate-300 bg-white shadow-lg">
              {tenants.map((t) => {
                const selected = t.tenantId === active?.tenantId;
                return (
                  <button
                    key={t.tenantId}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleChange(t.tenantId);
                    }}
                    className={[
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                      selected
                        ? 'bg-slate-100 font-semibold text-slate-950'
                        : 'text-slate-700 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-slate-500">
                      {selected && (
                        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.25 7.25a1 1 0 01-1.42 0l-3.25-3.25a1 1 0 111.42-1.42l2.54 2.54 6.54-6.54a1 1 0 011.42 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                    <span className="truncate">{t.tenantName}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <span className="flex-shrink-0 flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
        Connected
      </span>
    </div>
  );
}

function XeroIcon() {
  return (
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-[#58b873]">
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
        <path
          d="M8 12l3.5-3.5m0 0L15 12m-3.5-3.5V16m3.5-4l-3.5 3.5m0 0L8 12m3.5 3.5V8"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
